/**
 * DatabaseBanner -- says so when PratBot's database is not answering (R24 D6).
 *
 * Polls `/health`, which runs a bounded, cached `SELECT 1` on the server: every
 * 30s while the database answers, every 5s while it does not, and at once when
 * any API call comes back 503 `database_unavailable` (DATABASE_UNAVAILABLE_EVENT
 * from api/client.ts). A toast only appears when you happen to click something;
 * this is there whether you click or not.
 *
 * Shows nothing when `/health` itself cannot be reached -- the server being down
 * is a different problem, and the WebSocket status in the sidebar already says
 * so.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Database } from 'lucide-react'
import { DATABASE_UNAVAILABLE_EVENT, fetchHealth } from '@/api/client'

const POLL_OK_MS = 30_000
const POLL_DOWN_MS = 5_000

export function DatabaseBanner() {
  const [down, setDown] = useState(false)
  const [since, setSince] = useState<Date | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const downRef = useRef(false)

  const check = useCallback(async () => {
    window.clearTimeout(timer.current)
    const health = await fetchHealth()
    if (health !== null) {
      const isDown = health.database === 'unavailable'
      if (isDown && !downRef.current) setSince(new Date())
      downRef.current = isDown
      setDown(isDown)
    }
    timer.current = window.setTimeout(
      () => void check(),
      downRef.current ? POLL_DOWN_MS : POLL_OK_MS,
    )
  }, [])

  useEffect(() => {
    void check()
    const onUnavailable = () => void check()
    window.addEventListener(DATABASE_UNAVAILABLE_EVENT, onUnavailable)
    return () => {
      window.clearTimeout(timer.current)
      window.removeEventListener(DATABASE_UNAVAILABLE_EVENT, onUnavailable)
    }
  }, [check])

  if (!down) return null

  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 px-4 lg:px-6 py-2.5 bg-state-danger-bg border-b border-state-danger/40 text-sm text-text-primary"
    >
      <Database size={16} className="text-state-danger shrink-0 mt-0.5" />
      <span>
        <strong className="font-medium">Database unreachable</strong>
        {since && <> since {since.toLocaleTimeString()}</>}. PratBot is running
        degraded: chat commands that need the database won't answer, and nothing
        here will load or save until it's back. Rechecking every 5 seconds.
      </span>
    </div>
  )
}
